import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  EmptyState,
  PageHeader,
  type BadgeProps,
  useEntityList,
} from "@pericles/framework-frontend";

import { getDiscoveryReadiness, validateDiscoveryReadiness } from "../api/discoveryReadinessClient";
import type {
  DiscoveryReadinessContract,
  DiscoveryReadinessValidationResponse,
  DiscoveryReadinessSeverity,
  DiscoveryReadinessCategory,
} from "../types/discoveryReadiness";

import { InlineAlert } from "./InlineAlert";

interface DiscoveryReadinessPanelProps {
  initialProjectId?: string;
  projectOptions?: { value: string; label: string }[];
  loadReadiness?: (projectId: string) => Promise<DiscoveryReadinessContract>;
  validateReadiness?: (projectId: string) => Promise<DiscoveryReadinessValidationResponse>;
}

const STATUS_BADGES: Record<string, BadgeProps["variant"]> = {
  ready: "success",
  partial: "warning",
  not_ready: "danger",
};

const SEVERITY_BADGES: Partial<Record<string, BadgeProps["variant"]>> = {
  critical: "danger",
  error: "danger",
  warn: "warning",
  info: "default",
};

const CATEGORY_LABELS: Record<string, string> = {
  strategy: "Strategy",
  operations: "Operations",
  data: "Data",
  security: "Security",
  scope: "Scope",
  delivery: "Delivery",
  unknown: "Unknown",
};

function formatScore(score: number | null | undefined): string {
  if (score == null || Number.isNaN(score)) return "0%";
  const normalized = score <= 1 ? score * 100 : score;
  return `${Math.round(normalized)}%`;
}

export function DiscoveryReadinessPanel({
  initialProjectId,
  projectOptions,
  loadReadiness = getDiscoveryReadiness,
  validateReadiness = validateDiscoveryReadiness,
}: DiscoveryReadinessPanelProps): JSX.Element {
  const [selectedProjectId, setSelectedProjectId] = useState<string>(initialProjectId ?? "");
  const [readiness, setReadiness] = useState<DiscoveryReadinessContract | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<DiscoveryReadinessValidationResponse | null>(null);
  const [severityFilter, setSeverityFilter] = useState<DiscoveryReadinessSeverity | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState<DiscoveryReadinessCategory | "all">("all");

  const { data: projectEntities, isLoading: isLoadingProjects } = useEntityList<{ id: string | number; name?: string; code?: string }>({
    entity: "Project",
    page: 0,
    pageSize: 200,
  });

  const projects = useMemo(() => {
    if (projectOptions && projectOptions.length) return projectOptions;

    return (projectEntities ?? []).map((project) => ({
      value: String(project.id),
      label: project.name ? `${project.name} (${project.code ?? project.id})` : String(project.id),
    }));
  }, [projectEntities, projectOptions]);

  useEffect(() => {
    if (!selectedProjectId && projects.length) {
      setSelectedProjectId(projects[0].value);
    }
  }, [projects, selectedProjectId]);

  const filteredSignals = useMemo(() => {
    if (!readiness) return [];
    return readiness.signals.filter((signal) => {
      const severityMatches = severityFilter === "all" || signal.severity === severityFilter;
      const categoryMatches = categoryFilter === "all" || signal.category === categoryFilter;
      return severityMatches && categoryMatches;
    });
  }, [categoryFilter, readiness, severityFilter]);

  const handleLoad = async (projectId: string) => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    setValidationResult(null);

    try {
      const data = await loadReadiness(projectId);
      setReadiness(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load discovery readiness";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProjectId) {
      void handleLoad(selectedProjectId);
    }
  }, [selectedProjectId]);

  const handleValidate = async () => {
    if (!selectedProjectId) return;

    setIsValidating(true);
    setError(null);
    setValidationResult(null);

    try {
      const result = await validateReadiness(selectedProjectId);
      setReadiness(result.readiness);
      setValidationResult(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Validation failed";
      setError(message);
    } finally {
      setIsValidating(false);
    }
  };

  const statusBadge = readiness ? STATUS_BADGES[readiness.status] ?? "default" : "default";
  const decisionBadge = readiness?.decision === "pass" ? "success" : readiness?.decision === "fail" ? "danger" : "default";

  return (
    <div className="content-stack">
      <PageHeader
        title="Discovery Readiness"
        subtitle="Readiness gate based on discovery context, meetings, and pre-discovery answers."
      />

      <Card title="Project selection">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-slate-800" htmlFor="readiness-project">
            Project
          </label>
          <select
            id="readiness-project"
            className="input"
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
            disabled={isLoading || isValidating || isLoadingProjects}
          >
            {!selectedProjectId ? <option value="">Select a project</option> : null}
            {projects.map((project) => (
              <option key={project.value} value={project.value}>
                {project.label}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => handleLoad(selectedProjectId)}
              disabled={!selectedProjectId || isLoading}
            >
              {isLoading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className="button button-secondary"
              onClick={handleValidate}
              disabled={!selectedProjectId || isValidating}
            >
              {isValidating ? "Validating..." : "Validate"}
            </button>
          </div>
          {readiness ? (
            <div className="ml-auto flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span>Generated: {new Date(readiness.generatedAt).toLocaleString()}</span>
              <span>Defaults: {readiness.defaultsVersion}</span>
            </div>
          ) : null}
        </div>
      </Card>

      {error ? (
        <InlineAlert tone="danger" title="Unable to load readiness" description={error} />
      ) : null}

      {validationResult ? (
        <InlineAlert
          tone={validationResult.ok ? "success" : "danger"}
          title={validationResult.ok ? "Schema validation passed" : "Schema validation failed"}
          description={
            validationResult.ok
              ? "The computed readiness payload satisfies the v0a schema."
              : (validationResult.errors ?? []).join("\n")
          }
        />
      ) : null}

      <Card title="Readiness status">
        {readiness ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-3xl font-semibold text-slate-900">{formatScore(readiness.score)}</div>
              <Badge label={readiness.status} variant={statusBadge} />
              <Badge label={readiness.decision === "pass" ? "PASS" : "FAIL"} variant={decisionBadge} />
              <div className="text-sm text-slate-600">Answered: {readiness.summary.answeredCount}</div>
              <div className="text-sm text-slate-600">Open: {readiness.summary.openCount}</div>
              <div className="text-sm text-slate-600">Critical: {readiness.summary.criticalOpenCount}</div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Context status
                </div>
                <div className="mt-1 text-sm text-slate-800">
                  Customer: {readiness.inputs.customerContextStatus ?? "unknown"}
                  <br />
                  Project: {readiness.inputs.projectContextStatus ?? "unknown"}
                </div>
              </div>
              <div className="rounded border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pre-discovery</div>
                <div className="mt-1 text-sm text-slate-800">
                  Status: {readiness.inputs.preDiscoveryStatus ?? "unknown"}
                  <br />
                  Answered: {readiness.inputs.answeredQuestionsCount}
                </div>
              </div>
              <div className="rounded border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Activity</div>
                <div className="mt-1 text-sm text-slate-800">Meetings captured: {readiness.inputs.meetingsCount}</div>
              </div>
            </div>

            <div className="rounded border p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rules</div>
              <ul className="mt-2 flex flex-col gap-2">
                {readiness.explain.rules.map((rule) => (
                  <li key={rule.ruleId} className="flex items-center justify-between gap-2 text-sm">
                    <div>
                      <div className="font-semibold text-slate-900">{rule.ruleId}</div>
                      <div className="text-xs text-slate-600">{rule.details}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge label={`w=${rule.weight}`} variant="default" />
                      <Badge label={formatScore(rule.score)} variant={rule.passed ? "success" : "danger"} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <EmptyState
            title="No readiness data yet"
            description={
              selectedProjectId
                ? "Run refresh to compute discovery readiness for this project."
                : "Select a project to compute readiness."
            }
          />
        )}
      </Card>

      <Card title="Signals">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <label className="text-sm text-slate-700" htmlFor="readiness-severity">
            Severity
          </label>
          <select
            id="readiness-severity"
            className="input input-sm"
            value={severityFilter}
            onChange={(event) => setSeverityFilter(event.target.value as DiscoveryReadinessSeverity | "all")}
          >
            <option value="all">All</option>
            <option value="critical">Critical</option>
            <option value="error">Error</option>
            <option value="warn">Warn</option>
            <option value="info">Info</option>
          </select>

          <label className="text-sm text-slate-700" htmlFor="readiness-category">
            Category
          </label>
          <select
            id="readiness-category"
            className="input input-sm"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value as DiscoveryReadinessCategory | "all")}
          >
            <option value="all">All</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {filteredSignals.length === 0 ? (
          <EmptyState
            title="No signals"
            description="No readiness signals match the selected filters."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-slate-200">
            {filteredSignals.map((signal) => (
              <li key={signal.id} className="flex flex-col gap-1 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge label={signal.severity} variant={SEVERITY_BADGES[signal.severity] ?? "default"} />
                  <Badge label={CATEGORY_LABELS[signal.category] ?? signal.category} variant="default" />
                  <div className="font-semibold text-slate-900">{signal.title}</div>
                </div>
                <div className="text-sm text-slate-800">{signal.message}</div>
                {signal.evidence?.snippet ? (
                  <div className="rounded bg-slate-50 p-2 text-xs text-slate-700">
                    {signal.evidence.snippet}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
